import React, { useState, useEffect, createContext, useCallback } from "react";

export const FavouritesContext = createContext();

const STORAGE_KEY = "zvukyviry_favourites";

const loadFavourites = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const FavouritesProvider = ({ children }) => {
  const [favourites, setFavourites] = useState(loadFavourites);

  // Persist to localStorage whenever favourites change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favourites));
  }, [favourites]);

  const addFavourite = useCallback((video) => {
    setFavourites((prev) => {
      if (prev.some((v) => v.video_id === video.video_id)) return prev;
      return [...prev, {
        id: video.id,
        video_id: video.video_id,
        title: video.title,
        artist: video.artist,
        playlist_id: video.playlist_id,
      }];
    });
  }, []);

  const removeFavourite = useCallback((videoId) => {
    setFavourites((prev) => prev.filter((v) => v.video_id !== videoId));
  }, []);

  const isFavourite = useCallback(
    (videoId) => favourites.some((v) => v.video_id === videoId),
    [favourites]
  );

  const toggleFavourite = useCallback(
    (video) => {
      if (isFavourite(video.video_id)) {
        removeFavourite(video.video_id);
      } else {
        addFavourite(video);
      }
    },
    [isFavourite, removeFavourite, addFavourite]
  );

  const reorderFavourites = useCallback((oldIndex, newIndex) => {
    setFavourites((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(oldIndex, 1);
      updated.splice(newIndex, 0, moved);
      return updated;
    });
  }, []);

  return (
    <FavouritesContext.Provider
      value={{
        favourites,
        addFavourite,
        removeFavourite,
        isFavourite,
        toggleFavourite,
        reorderFavourites,
      }}
    >
      {children}
    </FavouritesContext.Provider>
  );
};

export default FavouritesContext;
